// COP — CALL-FOR-EVAC host flow: location resolution + the composer dialog.
// CONTRACT:C1-MAP-CLIENT.1.0
//
// The host-side half of the evac feature (the envelope builder lives in
// capabilities/copilotEvac.ts — EXTENDED, not duplicated):
//   1. resolveEvacLocation — PURE precedence: the operator's live own position
//      (ONLY while true-location sharing is ON — read through the sharer's
//      currentFix(), which is null otherwise, so the "position only if sharing
//      was on" rail holds by construction) → else the SELECTED entity's point →
//      else the map center. The chosen source is STATED in the request.
//   2. showEvacComposerDialog — the operator confirms the resolved location
//      (displayed with its source), picks urgency (routine/priority/urgent),
//      and adds freetext. Submitting IS the explicit operator confirmation of
//      the fallback point (selected/map-center); cancel/Esc sends nothing.
//
// The dialog shows coordinates ON SCREEN to the operator (that is the point);
// they are never written to console/toast/audit strings.

import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import type { EvacLocationSource, EvacPriority } from "../capabilities/copilotEvac.ts";
import type { OwnFix } from "./ownPosition.ts";
import { openHostDialog } from "./hostDialog.ts";

/** The resolved evac location + its STATED source. */
export interface EvacLocation {
  /** [lng, lat] GeoJSON order (matches SelfLocation.position). */
  position: [number, number];
  source: EvacLocationSource;
  /** Human label for the composer + the envelope's location_label. */
  label: string;
}

/** What the operator chose in the composer. */
export interface EvacComposerResult {
  priority: EvacPriority;
  note: string;
}

/**
 * Resolve WHERE the evac request points, by precedence (PURE, T0-tested):
 *   1. ownFix (non-null ONLY while sharing is ON and the fix is fresh)
 *   2. the selected entity's Point geometry
 *   3. the map center
 * The source is carried into the envelope so every reader knows which it was.
 */
export function resolveEvacLocation(input: {
  ownFix: OwnFix | null;
  selected: CanonicalEnvelope | null;
  mapCenter: { lng: number; lat: number };
}): EvacLocation {
  if (input.ownFix) {
    return {
      position: [input.ownFix.lon, input.ownFix.lat],
      source: "own-position",
      label: "OWN POSITION (live)",
    };
  }
  const g = input.selected?.geometry;
  if (g && g.type === "Point" && Array.isArray(g.coordinates)) {
    const [lon, lat] = g.coordinates as [number, number];
    if (Number.isFinite(lon) && Number.isFinite(lat)) {
      const name = input.selected?.properties?.name;
      return {
        position: [lon, lat],
        source: "selected-point",
        label: `SELECTED: ${typeof name === "string" && name ? name : (input.selected?.id ?? "entity")}`,
      };
    }
  }
  return {
    position: [input.mapCenter.lng, input.mapCenter.lat],
    source: "map-center",
    label: "MAP CENTER",
  };
}

const PRIORITIES: EvacPriority[] = ["routine", "priority", "urgent"];
const MAX_NOTE_LEN = 400;

/**
 * The CALL-FOR-EVAC composer. Shows the resolved location (with its source,
 * stated), urgency radios, and a freetext note. Resolves the operator's choice,
 * or null on cancel/Esc/headless (nothing is sent).
 */
export function showEvacComposerDialog(loc: EvacLocation, doc?: Document): Promise<EvacComposerResult | null> {
  let prioritySel: EvacPriority = "priority";
  let noteEl: HTMLTextAreaElement | null = null;

  return openHostDialog({
    title: "Call for evac",
    ...(doc ? { doc } : {}),
    build: (body, d) => {
      // LOCATION — displayed to the operator; submitting the dialog is the
      // explicit confirmation of this point (the fallback-point rail).
      const locBox = d.createElement("div");
      const [lon, lat] = loc.position;
      locBox.textContent = `LOCATION · ${loc.label} — ${lat.toFixed(5)}, ${lon.toFixed(5)} (${loc.source})`;
      locBox.style.cssText =
        "border: 1px solid #2a3a4a; border-radius: 3px; padding: 8px 10px; margin: 0 0 10px;" +
        "font-size: 12px; color: #9fb3c4;";
      body.appendChild(locBox);

      // URGENCY — routine / priority / urgent radios.
      const urg = d.createElement("div");
      urg.style.cssText = "display: flex; gap: 14px; margin: 0 0 10px; font-size: 12px;";
      for (const p of PRIORITIES) {
        const lab = d.createElement("label");
        lab.style.cssText = "display: flex; align-items: center; gap: 5px; cursor: pointer; text-transform: uppercase;";
        const radio = d.createElement("input");
        radio.type = "radio";
        radio.name = "evac-priority";
        radio.value = p;
        radio.checked = p === prioritySel;
        radio.addEventListener("change", () => {
          if (radio.checked) prioritySel = p;
        });
        lab.appendChild(radio);
        lab.appendChild(d.createTextNode(p));
        urg.appendChild(lab);
      }
      body.appendChild(urg);

      // FREETEXT — situation / casualties / access notes.
      const ta = d.createElement("textarea");
      ta.rows = 3;
      ta.maxLength = MAX_NOTE_LEN;
      ta.placeholder = "Situation, casualties, access… (sent in the clear — UNCLASSIFIED)";
      ta.style.cssText =
        "width: 100%; box-sizing: border-box; background: #0a1018; color: #cfd9e4;" +
        "border: 1px solid #2a3a4a; border-radius: 3px; padding: 8px; font: 12px/1.4 ui-monospace, Menlo, monospace;" +
        "resize: vertical;";
      body.appendChild(ta);
      noteEl = ta;

      const hint = d.createElement("div");
      hint.textContent = "Broadcast as a priority alert to everyone on the picture and the TAK net.";
      hint.style.cssText = "margin-top: 8px; font-size: 11px; opacity: 0.7;";
      body.appendChild(hint);
    },
    buttons: [
      { label: "Cancel", value: "cancel", tone: "quiet" },
      { label: "Send evac request", value: "send", tone: "danger" },
    ],
    dismissValue: "cancel",
  }).then((v) => {
    if (v !== "send") return null;
    const note = (noteEl?.value ?? "").trim().slice(0, MAX_NOTE_LEN);
    return { priority: prioritySel, note };
  });
}
